import { createFileRoute } from "@tanstack/react-router";
import { AppNav } from "@/components/AppNav";
import { API_URL } from "@/lib/supabase";
import { SITE_URL } from "@/lib/seo-jsonld";
import { useEffect, useRef, useState } from "react";

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string;

export const Route = createFileRoute("/contato")({
  head: () => {
    const url = `${SITE_URL}/contato/`;
    const title = "Contato — Busca Blindados";
    const description =
      "Fale com o Busca Blindados: dúvidas sobre alertas de carros blindados, sua conta ou parcerias. Respondemos rápido.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index,follow" },
        { property: "og:type", content: "website" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: ContatoPage,
});

function ContatoPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const widgetRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const renderWidget = () => {
      if (!widgetRef.current || !(window as any).turnstile) return;
      widgetIdRef.current = (window as any).turnstile.render(widgetRef.current, {
        sitekey: SITE_KEY,
        // Force the managed checkbox widget so the user can always see and
        // interact with the challenge (the invisible mode that other parts
        // of the app use has been finicky here).
        size: "normal",
        appearance: "always",
        theme: "auto",
        callback: (token: string) => setTurnstileToken(token),
        "expired-callback": () => setTurnstileToken(""),
        "error-callback": () => setTurnstileToken(""),
      });
    };

    if ((window as any).turnstile) {
      renderWidget();
    } else {
      const existing = document.getElementById("cf-turnstile-script");
      if (!existing) {
        const script = document.createElement("script");
        script.id = "cf-turnstile-script";
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        script.onload = renderWidget;
        document.head.appendChild(script);
      } else {
        existing.addEventListener("load", renderWidget);
      }
    }

    return () => {
      if (widgetIdRef.current !== undefined && (window as any).turnstile) {
        (window as any).turnstile.remove(widgetIdRef.current);
      }
    };
  }, []);

  const resetWidget = () => {
    setTurnstileToken("");
    if (widgetIdRef.current !== undefined && (window as any).turnstile) {
      (window as any).turnstile.reset(widgetIdRef.current);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!turnstileToken) {
      setError("Complete o desafio de segurança antes de enviar.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, message, token: turnstileToken }),
      });
      if (!res.ok) {
        // Token gets consumed by siteverify on every submit attempt; on any
        // failure the user needs a fresh one, so reset the widget. Surface
        // a friendly message for the common case where the token aged out
        // between rendering and clicking submit.
        const body = await res.json().catch(() => ({} as { error?: string; codes?: string[] }));
        const codes = (body as { codes?: string[] }).codes ?? [];
        resetWidget();
        if (codes.includes("timeout-or-duplicate")) {
          setError("O desafio de segurança expirou. Marque a caixa novamente e reenvie.");
        } else if (codes.length > 0) {
          setError(`Falha na validação de segurança (${codes.join(", ")}). Tente novamente.`);
        } else {
          setError("Algo deu errado. Tente novamente em instantes.");
        }
        return;
      }
      setSent(true);
    } catch {
      resetWidget();
      setError("Algo deu errado. Tente novamente em instantes.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase =
    "w-full rounded-xl border dark:border-white/10 border-black/10 dark:bg-white/5 bg-black/5 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 dark:focus:border-white/20 focus:border-black/20 focus:outline-none focus:ring-0 transition-colors";

  return (
    <div className="min-h-screen text-foreground">
      <AppNav />

      <main className="mx-auto max-w-2xl px-6 pb-32 pt-10">
        <h1 className="font-display text-3xl text-foreground mb-2">Contato</h1>
        <p className="text-sm text-muted-foreground mb-10">
          Dúvidas, sugestões ou problemas? Escreva pra gente.
        </p>

        {sent ? (
          <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-5 py-6 text-sm text-emerald-400">
            <p className="font-medium mb-1">Mensagem enviada!</p>
            <p className="text-emerald-400/70">Recebemos sua mensagem e responderemos em breve no email informado.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Nome
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Seu nome"
                className={inputBase}
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                className={inputBase}
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Mensagem
              </label>
              <textarea
                required
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Como podemos ajudar?"
                className={`${inputBase} resize-none`}
              />
            </div>

            <div ref={widgetRef} />

            {error && (
              <p className="text-xs text-red-400">{error}</p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl bg-foreground px-4 py-3 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? "Enviando…" : "Enviar mensagem"}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
